import React, { FC, useRef, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useForm, useWatch, useFormState } from 'react-hook-form';
import dayjs from 'dayjs';

import { CreditCardInfo } from 'src/interfaces/reservation_flow';
import { RSFormMessageContainer } from 'src/reservation-page/forms/RSFormMessageContainer';
import { RSTextField } from 'src/reservation-page/forms/RSTextField';
import { RSSelect } from 'src/reservation-page/forms/RSSelect';

import Visa from 'src/templates/merchants/resources/book/components/card-brands/Visa.svg';
import MasterCard from 'src/templates/merchants/resources/book/components/card-brands/MasterCard.svg';
import JCB from 'src/templates/merchants/resources/book/components/card-brands/JCB.svg';
import Amex from 'src/templates/merchants/resources/book/components/card-brands/Amex.svg';
import Diners from 'src/templates/merchants/resources/book/components/card-brands/Diners.svg';
import Discover from 'src/templates/merchants/resources/book/components/card-brands/Discover.svg';

export interface CreditCardInfoInputProps {
  defaultValues?: Partial<CreditCardInfo>;
  onChange: (value: Partial<CreditCardInfo>) => void;
  stripeError?: stripe.StripeError;
  currentYear: string;
  isJCBEnabled: boolean;
}

export const CreditCardInfoInput: FC<CreditCardInfoInputProps> = ({
  defaultValues,
  onChange,
  stripeError,
  currentYear,
  isJCBEnabled,
}) => {
  const intl = useIntl();

  const { register, control, reset, setError, clearErrors } =
    useForm<CreditCardInfo>({
      mode: 'onBlur',
    });

  const watchedValues = useWatch({ control });
  const {
    errors: {
      cardHolderName: cardHolderNameError,
      cardNumber: cardNumberError,
      expirationMonth: expirationMonthError,
      expirationYear: expirationYearError,
      securityCode: securityCodeError,
    },
  } = useFormState({ control });

  const expirationYears = useMemo(() => {
    const current = dayjs(currentYear, 'YYYY');

    return Array.from(new Array(10).keys()).map((index) => {
      const added = current.add(index, 'year');
      return added.format('YYYY');
    });
  }, [currentYear]);

  const expirationMonths = useMemo(() => {
    return Array.from(new Array(12).keys()).map((index) => {
      return (index + 1).toString();
    });
  }, []);

  // onChangeのコールバックの参照先の変更時にonChangeが発火しないようにrefで扱う
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (typeof watchedValues === 'undefined') return;

    onChangeRef.current && onChangeRef.current(watchedValues);
  }, [watchedValues]);

  const isFormValueInitializedRef = useRef(false);
  useEffect(() => {
    if (typeof defaultValues === 'undefined') return;
    if (isFormValueInitializedRef.current) return;

    // useFormのdefaultValuesを用いると未選択状態(undefinedをセット)にした際に、defaultValueの値が優先されるためresetを利用する
    reset(defaultValues);
    isFormValueInitializedRef.current = true;
  }, [defaultValues, reset]);

  useEffect(() => {
    if (typeof stripeError === 'undefined') {
      clearErrors();
      return;
    }

    switch (stripeError.code) {
      case 'incorrect_cvc':
        setError(
          'securityCode',
          {
            message: intl.formatMessage({
              defaultMessage:
                'セキュリティコードが正しくありません。今一度ご確認ください。',
              id: 'KCo8Pc',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            }),
          },
          {
            shouldFocus: false,
          },
        );
        break;
      case 'incorrect_number':
        setError(
          'cardNumber',
          {
            message: intl.formatMessage({
              defaultMessage:
                'カード番号が正しくありません。今一度ご確認ください。',
              id: 'kIyGFT',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            }),
          },
          {
            shouldFocus: false,
          },
        );
        break;
      case 'invalid_cvc':
        setError(
          'securityCode',
          {
            message: intl.formatMessage({
              defaultMessage:
                'セキュリティコードが正しくありません。今一度ご確認ください。',
              id: 'KCo8Pc',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            }),
          },
          {
            shouldFocus: false,
          },
        );
        break;
      case 'invalid_expiry_month':
        setError(
          'expirationMonth',
          {
            message: intl.formatMessage({
              defaultMessage: '有効期限（月）が正しくありません。',
              id: 'soxBwN',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            }),
          },
          {
            shouldFocus: false,
          },
        );
        break;
      case 'invalid_expiry_year':
        setError(
          'expirationYear',
          {
            message: intl.formatMessage({
              defaultMessage: '有効期限（年）が正しくありません。',
              id: 's6TCTd',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            }),
          },
          {
            shouldFocus: false,
          },
        );
        break;
      case 'invalid_number':
        setError(
          'cardNumber',
          {
            message: intl.formatMessage({
              defaultMessage:
                'カード番号が正しくありません。今一度ご確認ください。',
              id: 'kIyGFT',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            }),
          },
          {
            shouldFocus: false,
          },
        );
        break;
      default:
        clearErrors();
        break;
    }
  }, [stripeError, setError, clearErrors, intl]);

  return (
    <div className="space-y-1.5">
      <div className="rounded-15 box-border h-[64px] border border-solid border-gray-50 bg-white px-2.5">
        <div className="flex h-full items-center space-x-1.5">
          <div>
            <Visa
              width={34}
              height={12}
              viewBox="0 0 160 52"
              className="block"
            />
          </div>
          <div>
            <MasterCard
              width={34}
              height={23}
              viewBox="0 0 162 100"
              className="block"
            />
          </div>
          {isJCBEnabled && (
            <div>
              <JCB
                width={29}
                height={22}
                viewBox="0 0 135 100"
                className="block"
              />
            </div>
          )}
          <div>
            <Amex
              width={24}
              height={24}
              className="block"
              viewBox="0 0 101 100"
            />
          </div>
          {isJCBEnabled && (
            <div>
              <Diners
                width={29}
                height={21}
                viewBox="0 0 136 100"
                className="block"
              />
            </div>
          )}
          {isJCBEnabled && (
            <div>
              <Discover
                width={33}
                height={21}
                viewBox="0 0 157 100"
                className="block"
              />
            </div>
          )}
        </div>
      </div>
      <div>
        <RSFormMessageContainer
          isBlock
          errors={
            typeof cardHolderNameError !== 'undefined' &&
            typeof cardHolderNameError.message !== 'undefined'
              ? [cardHolderNameError.message]
              : undefined
          }
        >
          <RSTextField
            labelText={intl.formatMessage({
              defaultMessage: 'カード名義',
              id: 'j5uSIq',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            })}
            isBlock
            isError={typeof cardHolderNameError !== 'undefined'}
            {...register('cardHolderName', {
              required: {
                value: true,
                message: intl.formatMessage({
                  defaultMessage: 'カード名義を入力してください',
                  id: 'y2MFzh',
                  description:
                    'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
                }),
              },
            })}
          />
        </RSFormMessageContainer>
      </div>

      <div>
        <RSFormMessageContainer
          isBlock
          errors={
            typeof cardNumberError !== 'undefined' &&
            typeof cardNumberError.message !== 'undefined'
              ? [cardNumberError.message]
              : undefined
          }
        >
          <RSTextField
            labelText={intl.formatMessage({
              defaultMessage: 'カード番号',
              id: 'YvgLYE',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            })}
            isBlock
            isError={typeof cardNumberError !== 'undefined'}
            {...register('cardNumber', {
              required: {
                value: true,
                message: intl.formatMessage({
                  defaultMessage: 'カード番号を入力してください',
                  id: 'SmUo4T',
                  description:
                    'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
                }),
              },
            })}
          />
        </RSFormMessageContainer>
      </div>

      <div className="flex space-x-1.5">
        <div className="w-1/2">
          <RSFormMessageContainer
            isBlock
            errors={
              typeof expirationMonthError !== 'undefined' &&
              typeof expirationMonthError.message !== 'undefined'
                ? [expirationMonthError.message]
                : undefined
            }
          >
            <RSSelect
              labelText={intl.formatMessage({
                defaultMessage: '月',
                id: '4lRNiZ',
                description:
                  'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
              })}
              isBlock
              isError={typeof expirationMonthError !== 'undefined'}
              hasSelectedValue={(() => {
                if (
                  typeof watchedValues === 'undefined' ||
                  typeof watchedValues.expirationMonth === 'undefined' ||
                  watchedValues.expirationMonth === ''
                ) {
                  return false;
                }

                return expirationMonths.includes(watchedValues.expirationMonth);
              })()}
              {...register('expirationMonth', {
                required: {
                  value: true,
                  message: intl.formatMessage({
                    defaultMessage: '月を入力してください',
                    id: '4C49uj',
                    description:
                      'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
                  }),
                },
              })}
            >
              <option value="" style={{ display: 'none' }}></option>
              {expirationMonths.map((month) => {
                return (
                  <option
                    key={`creditCardInfo_expirationMonths_${month}`}
                    value={month}
                  >
                    {month}
                  </option>
                );
              })}
            </RSSelect>
          </RSFormMessageContainer>
        </div>

        <div className="w-1/2">
          <RSFormMessageContainer
            isBlock
            errors={
              typeof expirationYearError !== 'undefined' &&
              typeof expirationYearError.message !== 'undefined'
                ? [expirationYearError.message]
                : undefined
            }
          >
            <RSSelect
              labelText={intl.formatMessage({
                defaultMessage: '年',
                id: 'CrWRU7',
                description:
                  'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
              })}
              isBlock
              isError={typeof expirationYearError !== 'undefined'}
              hasSelectedValue={(() => {
                if (
                  typeof watchedValues === 'undefined' ||
                  typeof watchedValues.expirationYear === 'undefined' ||
                  watchedValues.expirationYear === ''
                ) {
                  return false;
                }

                return expirationYears.includes(watchedValues.expirationYear);
              })()}
              {...register('expirationYear', {
                required: {
                  value: true,
                  message: intl.formatMessage({
                    defaultMessage: '年を入力してください',
                    id: 'y4pxqf',
                    description:
                      'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
                  }),
                },
              })}
            >
              <option value="" style={{ display: 'none' }}></option>
              {expirationYears.map((year) => {
                return (
                  <option key={`creditCardInfo_expirationYears_${year}`}>
                    {year}
                  </option>
                );
              })}
            </RSSelect>
          </RSFormMessageContainer>
        </div>
      </div>

      <div>
        <RSFormMessageContainer
          isBlock
          errors={
            typeof securityCodeError !== 'undefined' &&
            typeof securityCodeError.message !== 'undefined'
              ? [securityCodeError.message]
              : undefined
          }
        >
          <RSTextField
            labelText={intl.formatMessage({
              defaultMessage: 'セキュリティコード',
              id: 'GykyOX',
              description:
                'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
            })}
            isBlock
            isError={typeof securityCodeError !== 'undefined'}
            {...register('securityCode', {
              required: {
                value: true,
                message: intl.formatMessage({
                  defaultMessage: 'セキュリティコードを入力してください',
                  id: 'yfa6ae',
                  description:
                    'src/templates/merchants/resources/book/components/CreditCardInfoInput/index.tsx',
                }),
              },
            })}
          />
        </RSFormMessageContainer>
      </div>
    </div>
  );
};
